import { useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import toRem from 'utils/toRem';

import { error } from 'constants/variables';

const isValidNumberInput = input => {
  const numericPattern = /^[0-9]$|^$/;

  const allowedKeys = [
    'Enter',
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'Tab',
    'Meta',
    'Ctrl',
  ];

  return allowedKeys.includes(input) || numericPattern.test(input);
};

const MFACodeInput = ({ code, setCode, id, errors }) => {
  const inputsRef = useRef([]);
  const inputIds = useState(() => code.map(() => uuidv4()));

  const fillAllFields = text => {
    if (text.length === code.length && /^\d{6}$/.test(text)) {
      const newCode = text.split('');
      setCode(newCode);
      inputsRef.current[code.length - 1].focus();
    }
  };

  const handleChange = (e, index) => {
    let value = e.target.value;

    if (value.trim().length > 1) {
      fillAllFields(value);
      return;
    }

    // If the input is invalid and hasn't been blocked in
    // handleKeyDown, force the value of the field to be empty
    if (!isValidNumberInput(value)) {
      value = '';
      return;
    }

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    // Focus on the next input if current input has a value
    if (value && index < code.length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Allow copy/paste using Command (Mac) or Control (Windows)
    const isPaste = (e.ctrlKey || e.metaKey) && e.key === 'v';

    const isValidInput = isValidNumberInput(e.key) || isPaste;

    if (!isValidInput) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = e => {
    const pasteData = e.clipboardData.getData('text');

    fillAllFields(pasteData);
    e.preventDefault();
  };

  return (
    <>
      <FieldWrapper>
        <Label>Enter code</Label>
        {code.map((digit, index) => (
          <DigitInput
            key={inputIds[index]}
            className={`cef-ui-text-input-field${errors && ' has-error'}`}
            type="tel"
            value={digit}
            aria-label={`${id}-${index}`}
            onChange={e => handleChange(e, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={el => {
              inputsRef.current[index] = el;
            }}
            autoComplete="one-time-code"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
          />
        ))}
      </FieldWrapper>
      {errors && (
        <Error className="cef-ui-input-error">
          {errors.mfaCodeInput.message}
        </Error>
      )}
    </>
  );
};

const FieldWrapper = styled.fieldset`
  display: flex;
  gap: ${toRem(10)};
`;

const Label = styled.legend`
  border: 0;
  font-size: ${toRem(16)};
  font-weight: 700;
  margin-bottom: ${toRem(10)};
  line-height: ${toRem(18)};
`;

const DigitInput = styled.input`
  &[type='tel'] {
    width: 42px;
    height: 42px;
    text-align: center;
    font-size: ${toRem(20)};
    box-sizing: border-box;
  }

  &.has-error {
    border-color: ${error};
  }
`;

const Error = styled.span`
  color: ${error};
`;

export default MFACodeInput;
